<template>
    <div class="flex-grow-1">
        <h1 class="text-3xl sm:text-4xl md:text-6xl mb-16 sm:mb-32">FAQ (Häufige Fragen)</h1>
        <p class="text-subtext-color font-heading text-lg leading-spaced w-full mb-24">
            Du hast eine Frage oder ein Problem? Keine Sorge, unsere FAQ helfen Dir Dein Problem zu lösen. Solltest du hier keine Antwort auf Deine Frage finden, so kannst du gerne unseren Support kontaktieren.
        </p>

        <form-input name="search" inverted x-large v-model="searchQuery" :icon="icons.search" clearable placeholder="Suchen"/>

        <div v-for="(category, key) in filteredCategories" :key="key" class="pb-48">
            <h2 class="text-left text-xl sm:text-3xl">{{ category.title }}</h2>
            <expansion-panels>
                <expansion-panel v-for="(question, key) in category.questions" :key="key" :title="(key+1) + '. ' + question.title">
                    <user-content :text="question.content"/>
                </expansion-panel>
            </expansion-panels>
        </div>
    </div>
</template>

<script setup lang="ts">
import {debounce, stripHtml} from "@spoferan/spoferan-ts-core";
import {cloneObject} from "@spoferan/spoferan-ts-core";
import {searchIcon} from "@spoferan/nuxt-spoferan/icons";

const {$image} = useNuxtApp();
const route = useRoute();

const {data: faqContent} = await useApiFetch(`/v1/settings/${route.params.area}_faq`, {
	guest: true
});

// Extract the regex matches of the content -> Ex.: [['<h2>Title</h2>', 'h2', 'Title'], ['<h3>Subtitle</h3>', 'h3', 'Subtitle'], ...]
const matches = Array.from(faqContent.value.matchAll(/<(.+)>(.*?)<\/\1>/g));

const categories = [];
let currentCategory = null; // h2
let currentCategoryQuestion = null; // h3 (expandable)
matches.forEach(match => {
	switch (match[1]) {
		case 'h2':

			// If a new category title tag is found, we push the previous category to the finished categories
			if (currentCategory) {
				categories.push(currentCategory);
			}

			// Create a new category that starts with the found title tag
			currentCategory = {
				title: match[2],
				content: '',
				questions: []
			}

			currentCategoryQuestion = null;

			break;
		case 'h3':

			// If a new section title tag is found, we push the previous section to the current category's questions
			if (currentCategoryQuestion) {
				currentCategory.questions.push(currentCategoryQuestion);
			}

			// Create a new block that starts with the found title tag
			currentCategoryQuestion = {
				title: match[2],
				content: ''
			}
			break;
		default:
			// If no title or subtitle tag is found, we just push the full HTML to the content
			if (currentCategoryQuestion) {
				currentCategoryQuestion.content += match[0]
			} else {
				currentCategory.content += match[0]
			}
	}
})

if (currentCategory) {
	if (currentCategoryQuestion) {
		currentCategory.questions.push(currentCategoryQuestion);
	}

	categories.push(currentCategory);
}

const filteredCategories = ref(cloneObject(categories)); // The FAQ categories to display that can get filtered
const icons = {
	search: searchIcon,
};
const searchQuery = ref(null);

watch(searchQuery, (newValue) => {
	search(newValue);
});

/**
 * Filters the questions and answers of the FAQ's by the specified search query
 */
const search = debounce(function (query) {
	query = query ? query : ''
	query = query.toLowerCase()
	const searchCategories = {}
	categories.forEach((category, categoryKey) => {
		const questions = cloneObject(category.questions).filter(question => {
			return question.title.toLowerCase().includes(query) || question.content.toLowerCase().includes(query)
		})
		if (questions && questions.length) {
			searchCategories[categoryKey] = cloneObject(category)
			searchCategories[categoryKey].questions = questions
		}
	})
	filteredCategories.value = cloneObject(searchCategories)
}, 200)

useMeta({
	title: 'FAQ (Häufige Fragen)',
	description: 'Du hast eine Frage oder ein Problem? Keine Sorge, unsere FAQ helfen Dir Dein Problem zu lösen. Solltest du hier keine Antwort auf Deine Frage finden, so kannst du gerne unseren Support kontaktieren.',
	image: $image.format('/views/help/spoferan-faq.jpg', {width: 1200, height: 1200})
})

useSchemaOrg([
	defineWebPage({
		'@type': 'FAQPage',
	}),
	...categories.map(category => {
		return category.questions.map(question => {
			return defineQuestion({
				name: question.title,
				acceptedAnswer: stripHtml(question.content),
			});
		})
	}).flat()
])

</script>
